import { Badge, Flex, Text } from '@radix-ui/themes';
import { CommonTableHoC } from 'components/common/table';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { t } from 'i18next';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IMachineStateCurrent } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { IReadyData } from 'lib_ts/interfaces/machine-msg/i-ready';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { isNil, round } from 'lodash';
import { useMemo } from 'react';

interface IMachineStateRow extends RowWithID {
  moduleName: string;
  moduleDescription?: string;
  value?: string | number | boolean | string[] | ITrajektRefBreak | undefined;
  target?: string | number | boolean | string[] | ITrajektRefBreak | undefined;
  roundTo?: number;
  unit?: string;
  status?: boolean;
}

const MACHINE_SETTINGS_ROWS: IMachineStateRow[] = [
  {
    _id: 'w1',
    moduleName: 'w1',
    moduleDescription: t('admin.bottom-wheel').toString(),
    unit: 'rpm',
    roundTo: 0,
  },
  {
    _id: 'w2',
    moduleName: 'w2',
    moduleDescription: t('admin.3b-wheel').toString(),
    unit: 'rpm',
    roundTo: 0,
  },
  {
    _id: 'w3',
    moduleName: 'w3',
    moduleDescription: t('admin.1b-wheel').toString(),
    unit: 'rpm',
    roundTo: 0,
  },
  {
    _id: 'a1',
    moduleName: 'a1',
    moduleDescription: t('admin.bottom-alpha').toString(),
    unit: 'deg',
    roundTo: 4,
  },
  {
    _id: 'a2',
    moduleName: 'a2',
    moduleDescription: t('admin.3b-alpha').toString(),
    unit: 'deg',
    roundTo: 4,
  },
  {
    _id: 'a3',
    moduleName: 'a3',
    moduleDescription: t('admin.1b-alpha').toString(),
    unit: 'deg',
    roundTo: 4,
  },
  {
    _id: 'px',
    moduleName: 'px',
    moduleDescription: t('admin.x-position-machine').toString(),
    unit: 'ft',
    roundTo: 3,
  },
  {
    _id: 'py',
    moduleName: 'py',
    moduleDescription: t('admin.y-position-machine').toString(),
    unit: 'ft',
    roundTo: 3,
  },
  {
    _id: 'pz',
    moduleName: 'pz',
    moduleDescription: t('admin.z-position-machine').toString(),
    unit: 'ft',
    roundTo: 3,
  },
  {
    _id: 'qw',
    moduleName: 'qw',
    moduleDescription: t('admin.seam-orientation-qw').toString(),
    unit: 'ft',
    roundTo: 4,
  },
  {
    _id: 'qx',
    moduleName: 'qx',
    moduleDescription: t('admin.seam-orientation-qx').toString(),
    unit: 'ft',
    roundTo: 4,
  },
  {
    _id: 'qy',
    moduleName: 'qy',
    moduleDescription: t('admin.seam-orientation-qy').toString(),
    unit: 'ft',
    roundTo: 4,
  },
  {
    _id: 'qz',
    moduleName: 'qz',
    moduleDescription: t('admin.seam-orientation-qz').toString(),
    unit: 'ft',
    roundTo: 4,
  },
  {
    _id: 'yaw',
    moduleName: 'yaw',
    moduleDescription: t('admin.angle-of-yaw').toString(),
    unit: 'deg',
    roundTo: 3,
  },
  {
    _id: 'tilt',
    moduleName: 'tilt',
    moduleDescription: t('admin.angle-of-tilt').toString(),
    unit: 'deg',
    roundTo: 3,
  },
  {
    _id: 'training',
    moduleName: 'training',
    moduleDescription: t('admin.training-shot').toString(),
  },
  {
    _id: 'ball_count',
    moduleName: 'ball_count',
    moduleDescription: t('admin.number-of-balls').toString(),
  },
  {
    _id: 'od_busy',
    moduleName: 'od_busy',
    moduleDescription: t('admin.output-detector').toString(),
  },
  {
    _id: 'rapid',
    moduleName: 'rapid',
    moduleDescription: t('settings.rapid-mode').toString(),
  },
  {
    _id: 'dead_loading_procs',
    moduleName: 'dead_loading_procs',
    moduleDescription: t('admin.dead-processes').toString(),
  },
  {
    _id: 'video_uuid',
    moduleName: 'video_uuid',
    moduleDescription: t('admin.video-id').toString(),
  },
  {
    _id: 'projector_dt',
    moduleName: 'projector_dt',
    moduleDescription: t('admin.projector-delta-time').toString(),
    unit: 's',
    roundTo: 4,
  },
];

const getMsRows = (
  ms: IMachineStateCurrent,
  readyData: IReadyData,
  lastTarget: IMachineState | undefined
): IMachineStateRow[] => {
  const output = MACHINE_SETTINGS_ROWS.map((module) => {
    const value = ms[module.moduleName as keyof IMachineStateCurrent];
    const status = readyData[module.moduleName as keyof IReadyData];

    const o: IMachineStateRow = {
      ...module,
      value: value,
      status: status,
      target: lastTarget
        ? ms[module.moduleName as keyof IMachineState]
        : undefined,
    };

    return o;
  });

  return output;
};

interface IProps {
  ms?: IMachineStateCurrent;
  readyData?: IReadyData;
  loading: boolean;
  lastTarget?: IMachineState;
}

export const MachineStateOverview = (props: IProps) => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const columns = useMemo<ITypedTableColumn<IMachineStateRow>[]>(
    () => [
      {
        label: 'admin.module',
        key: '_module',
        formatFn: (m) => {
          return (
            <Flex direction="column" gap={RADIX.FLEX.GAP.XS} pl="1" py="2">
              <Text size="2">{m.moduleName}</Text>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                {m.moduleDescription}
              </Text>
            </Flex>
          );
        },
      },
      {
        label: 'Last Target',
        key: '_target',
        invisible: !props.lastTarget,
        formatFn: (m) => {
          if (isNil(m.target)) {
            return null;
          }

          const target = !isNil(m.roundTo)
            ? round(m.target as number, m.roundTo)
            : `${m.target}`;

          return (
            <Flex gap={RADIX.FLEX.GAP.XS} align="end" px="0" py="2">
              <Text size="2">{target}</Text>
              {m.unit && (
                <Text size="1" color={RADIX.COLOR.SECONDARY}>
                  {m.unit}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        label: 'admin.value',
        key: '_value',
        formatFn: (m) => {
          if (isNil(m.value)) {
            return null;
          }

          const value = !isNil(m.roundTo)
            ? round(m.value as number, m.roundTo)
            : `${m.value}`;

          return (
            <Flex gap={RADIX.FLEX.GAP.XS} align="end" px="0" py="2">
              <Text size="2">{value}</Text>
              {m.unit && (
                <Text size="1" color={RADIX.COLOR.SECONDARY}>
                  {m.unit}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        label: 'common.status',
        key: '_status',
        align: 'right',
        formatFn: (m) => {
          if (isNil(m.status)) {
            return null;
          }

          return (
            <Flex
              gap={RADIX.FLEX.GAP.XS}
              px="0"
              py="2"
              justify="end"
              // static width to stop the columns from resizing/flickering as R2F status changes
              width="90px"
            >
              {m.status ? (
                <Badge color={RADIX.COLOR.SUCCESS}>{t('common.ready')}</Badge>
              ) : (
                <Badge color={RADIX.COLOR.DANGER}>
                  {t('common.not-ready')}
                </Badge>
              )}
            </Flex>
          );
        },
      },
    ],
    [props.lastTarget]
  );

  const rows = useMemo(() => {
    if (!props.ms || !props.readyData) {
      return [];
    }

    return getMsRows(props.ms, props.readyData, props.lastTarget);
  }, [props.ms, props.readyData, props.lastTarget]);

  if (!machine) {
    return null;
  }

  return (
    <CommonTableHoC
      dataTestID="MachineStateOverview"
      identifier="MachineStateOverviewTable"
      displayColumns={columns}
      displayData={rows}
      loading={props.loading}
    />
  );
};
